<template>
  <div class="vp-listing users-page">
    <!-- Page header -->
    <div class="vp-listing-head">
      <div class="vp-min-w-0">
        <!-- Plain nav rather than Bootstrap breadcrumb markup: the theme carries a dozen
             .breadcrumb-item rules that fight the design's spacing. -->
        <nav class="vp-crumbs" :aria-label="$t('common.navigation.users')">
          <router-link to="/">{{ $t('common.navigation.home') }}</router-link>
          <i class="ph-duotone ph-caret-right vp-crumbs-sep"></i>
          <span class="vp-crumbs-current">{{ $t('common.navigation.users') }}</span>
        </nav>
        <h1 class="vp-listing-title">{{ $t('users.title') }}</h1>
        <p class="vp-listing-subtitle">{{ $t('users.pageSubtitle') }}</p>
      </div>

      <button v-if="canCreateUser" type="button" class="us-page-btn is-primary" @click="openCreate">
        <i class="ph-duotone ph-user-plus"></i>{{ $t('users.addUser') }}
      </button>
    </div>

    <!-- Status tiles -->
    <div class="vp-kpis">
      <div v-for="kpi in kpis" :key="kpi.key" class="vp-kpi" :style="{ borderTopColor: kpi.edge }">
        <div class="vp-kpi-label">
          <i class="ph-duotone" :class="kpi.icon" :style="{ color: kpi.edge }"></i>{{ kpi.label }}
        </div>
        <div class="vp-kpi-value">{{ kpi.value }}</div>
        <div class="us-kpi-note" :class="kpi.tone">{{ kpi.note }}</div>
      </div>
    </div>

    <div class="vp-listing-card">
      <!-- Search and status filters -->
      <div class="us-toolbar">
        <h2 class="us-card-title">
          {{ $t('users.team') }} <span class="us-card-count">({{ visibleUsers.length }})</span>
        </h2>

        <label class="us-search">
          <i class="ph-duotone ph-magnifying-glass"></i>
          <input
            v-model="query"
            type="search"
            :placeholder="$t('users.searchPlaceholder')"
            :aria-label="$t('users.searchPlaceholder')"
          />
        </label>

        <div class="us-filters" role="group" :aria-label="$t('users.fields.status')">
          <button
            v-for="option in FILTERS"
            :key="option"
            type="button"
            class="us-filter"
            :class="{ 'is-active': filter === option }"
            :aria-pressed="filter === option"
            @click="filter = option"
          >
            {{ $t(`users.filters.${option}`) }}
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="us-state">
        <span class="spinner-border spinner-border-sm me-2"></span>{{ $t('users.loadingUsers') }}
      </div>

      <!-- Error -->
      <div v-else-if="error" class="us-state us-state--error">
        <i class="ph-duotone ph-warning-circle"></i>{{ error }}
      </div>

      <template v-else>
        <div v-if="visibleUsers.length > 0" class="us-scroll">
          <div class="us-table">
            <div class="us-head-row">
              <span>{{ $t('users.columns.user') }}</span>
              <span>{{ $t('users.columns.permissions') }}</span>
              <span class="us-right">{{ $t('common.actionsLabel') }}</span>
            </div>

            <div v-for="user in visibleUsers" :key="user.id" class="us-row" :style="{ borderLeftColor: edgeFor(user) }">
              <!-- Identity -->
              <div class="us-identity">
                <span class="us-avatar" :class="{ 'is-muted': !user.isActive }">{{ initialsFor(user) }}</span>
                <div class="vp-min-w-0">
                  <div class="us-name-row">
                    <span class="us-name">{{ nameFor(user) }}</span>
                    <span class="us-pill" :class="user.isActive ? 'is-active' : 'is-idle'">
                      {{ user.isActive ? $t('users.status.active') : $t('users.status.inactive') }}
                    </span>
                    <span v-if="!user.emailConfirmed" class="us-pill is-pending">
                      <i class="ph-duotone ph-envelope-simple"></i>{{ $t('users.emailUnconfirmed') }}
                    </span>
                  </div>
                  <div class="us-email vp-truncate">{{ user.email || user.userName || '—' }}</div>
                  <div v-if="user.phoneNumber" class="us-phone">{{ user.phoneNumber }}</div>
                </div>
              </div>

              <!-- Granted permissions -->
              <div class="vp-min-w-0">
                <template v-if="summaryFor(user)">
                  <div class="us-areas">{{ areasLabel(summaryFor(user)) }}</div>
                  <div class="us-perm-meter">
                    <span class="us-perm-track">
                      <i :style="{ width: percentFor(summaryFor(user)), background: meterColor(summaryFor(user)) }"></i>
                    </span>
                    <span class="us-perm-count">
                      {{ $t('users.permissionsCell.granted', { granted: summaryFor(user).granted, total: summaryFor(user).total }) }}
                    </span>
                  </div>
                </template>
                <div v-else class="us-areas us-areas--muted">{{ permissionsPlaceholder }}</div>
              </div>

              <!-- Row actions -->
              <div class="us-actions">
                <button
                  v-if="canEditUser"
                  type="button"
                  class="us-icon-btn is-accent"
                  :title="$t('common.actions.edit')"
                  :aria-label="$t('common.actions.edit')"
                  @click="openEdit(user)"
                >
                  <i class="ph-duotone ph-pencil-simple"></i>
                </button>
                <button
                  v-if="canManagePermissions"
                  type="button"
                  class="us-icon-btn"
                  :title="$t('users.editPermissions')"
                  :aria-label="$t('users.editPermissions')"
                  @click="editPermissions(user.id)"
                >
                  <i class="ph-duotone ph-shield-check"></i>
                </button>
                <button
                  v-if="canDeleteUser && !isCurrentUser(user)"
                  type="button"
                  class="us-icon-btn is-danger"
                  :title="$t('common.actions.delete')"
                  :aria-label="$t('common.actions.delete')"
                  @click="deleteUser(user)"
                >
                  <i class="ph-duotone ph-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Nothing matches the current search or filter -->
        <div v-else-if="users.length > 0" class="us-empty">
          <i class="ph-duotone ph-users-three"></i>
          <div class="us-empty-title">{{ $t('users.noMatch') }}</div>
          <button type="button" class="us-page-btn" @click="clearFilters">{{ $t('users.clearFilters') }}</button>
        </div>

        <!-- No users at all -->
        <div v-else class="us-empty">
          <i class="ph-duotone ph-users-three"></i>
          <div class="us-empty-title">{{ $t('users.noUsers') }}</div>
          <p class="us-empty-text">{{ $t('users.noUsersDescription') }}</p>
          <button v-if="canCreateUser" type="button" class="us-page-btn is-primary" @click="openCreate">
            <i class="ph-duotone ph-user-plus"></i>{{ $t('users.addFirstUser') }}
          </button>
        </div>

        <div v-if="users.length > 0" class="us-foot">
          <span class="us-foot-note">
            {{ $t('users.showingCount', { shown: visibleUsers.length, total: users.length }) }}
          </span>
          <button v-if="canCreateUser" type="button" class="us-invite" @click="openCreate">
            <i class="ph-duotone ph-user-plus"></i>{{ $t('users.inviteColleague') }}
          </button>
        </div>
      </template>
    </div>

    <UserEditModal :open="editOpen" :user-id="editingUserId" @close="closeEdit" @saved="loadUsers" />
  </div>

  <!-- User Permissions Modal -->
  <UserPermissionsModal :userId="selectedUserId" ref="permissionsModal" @error="handlePermissionError" />
</template>

<script setup>
// 1. Vue core
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

// 2. Third-party
import Swal from 'sweetalert2'
import { Modal } from 'bootstrap'

// 3. Services & composables
import userService from '@/services/userService'
import userPermissionService from '@/services/userPermissionService'
import { usePermissions } from '@/composables/usePermissions'

// 4. Components
import UserPermissionsModal from '@/components/UserPermissionsModal.vue'
import UserEditModal from '@/components/users/UserEditModal.vue'

// 5. Constants
const FILTERS = ['all', 'active', 'inactive', 'pending']
/** Above this share of granted permissions a member reads as a full administrator. */
const BROAD_ACCESS = 0.8
const WIDE_ACCESS = 0.7
const SOME_ACCESS = 0.35

const { t } = useI18n()
const store = useStore()
const { hasPermission } = usePermissions()

const users = ref([])
const loading = ref(false)
const error = ref('')
const query = ref('')
const filter = ref('all')

/** userId → { granted, total, areas }. Filled in after the list renders. */
const permissionSummaries = ref({})
const summariesLoaded = ref(false)

const selectedUserId = ref(null)
const permissionsModal = ref(null)

const editOpen = ref(false)
const editingUserId = ref(null)

const currentUser = computed(() => store.getters['auth/currentUser'])

const canCreateUser = computed(() => hasPermission('Supplier.Users.Create'))
const canEditUser = computed(() => hasPermission('Supplier.Users.Edit'))
const canDeleteUser = computed(() => hasPermission('Supplier.Users.Delete'))
const canManagePermissions = computed(() => hasPermission('Supplier.Users.ManagePermissions'))

/* ── Presentation helpers ──────────────────────────────────── */

const nameFor = (user) => {
  const fullName = [user.name, user.surname].filter(Boolean).join(' ')
  return user.displayUserName || fullName || user.userName || user.email || '—'
}

const initialsFor = (user) => {
  const words = nameFor(user).split(/[\s@._-]+/).filter(Boolean)
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?'
}

const summaryFor = (user) => permissionSummaries.value[user.id] || null

const shareFor = (summary) => (summary.total ? summary.granted / summary.total : 0)

const percentFor = (summary) => `${Math.round(shareFor(summary) * 100)}%`

const meterColor = (summary) => {
  const share = shareFor(summary)
  if (share > WIDE_ACCESS) return 'var(--vp-primary)'
  if (share > SOME_ACCESS) return 'var(--vp-accent)'
  return 'var(--vp-gray-400)'
}

/** The coloured spine that tells inactive, invited and full-access members apart at a glance. */
const edgeFor = (user) => {
  if (!user.isActive) return 'var(--vp-gray-300)'
  if (!user.emailConfirmed) return 'var(--vp-warning)'
  const summary = summaryFor(user)
  if (summary && shareFor(summary) > BROAD_ACCESS) return 'var(--vp-primary)'
  return 'var(--vp-accent)'
}

const areasLabel = (summary) => {
  if (!summary.granted) return t('users.permissionsCell.none')
  if (summary.granted === summary.total) return t('users.permissionsCell.allAreas')
  if (summary.areas.length) return summary.areas.join(', ')
  return t('users.permissionsCell.limited')
}

const permissionsPlaceholder = computed(() =>
  summariesLoaded.value ? t('users.permissionsCell.unavailable') : t('common.loading')
)

/* ── Data ──────────────────────────────────────────────────── */

const matchesFilter = (user) => {
  if (filter.value === 'active') return user.isActive
  if (filter.value === 'inactive') return !user.isActive
  if (filter.value === 'pending') return !user.emailConfirmed
  return true
}

const matchesQuery = (user) => {
  const term = query.value.trim().toLowerCase()
  if (!term) return true
  return `${nameFor(user)} ${user.email || ''} ${user.userName || ''}`.toLowerCase().includes(term)
}

const visibleUsers = computed(() => users.value.filter(u => matchesFilter(u) && matchesQuery(u)))

const kpis = computed(() => {
  const total = users.value.length
  const active = users.value.filter(u => u.isActive).length
  const pending = users.value.filter(u => !u.emailConfirmed).length
  const inactive = total - active

  return [
    {
      key: 'members',
      label: t('users.kpi.members'),
      value: total,
      note: t('users.kpi.membersNote'),
      tone: 'is-muted',
      icon: 'ph-users-three',
      edge: 'var(--vp-accent)'
    },
    {
      key: 'active',
      label: t('users.kpi.active'),
      value: active,
      note: t('users.kpi.activeNote'),
      tone: 'is-success',
      icon: 'ph-check-circle',
      edge: 'var(--vp-success)'
    },
    {
      key: 'pending',
      label: t('users.kpi.pending'),
      value: pending,
      note: pending ? t('users.kpi.pendingNote') : t('users.kpi.pendingNoteNone'),
      tone: pending ? 'is-warning' : 'is-success',
      icon: 'ph-envelope-simple',
      edge: 'var(--vp-warning)'
    },
    {
      key: 'inactive',
      label: t('users.kpi.inactive'),
      value: inactive,
      note: inactive ? t('users.kpi.inactiveNote') : t('users.kpi.inactiveNoteNone'),
      tone: inactive ? 'is-muted' : 'is-success',
      icon: 'ph-prohibit',
      edge: 'var(--vp-primary)'
    }
  ]
})

/**
 * Turns the flat permission list into the row summary: how many of the tenant's permissions
 * a member holds, and which top-level areas those sit in.
 */
const summarise = (permissions) => {
  const all = permissions || []
  return {
    granted: all.filter(p => p.isGranted).length,
    total: all.length,
    areas: all.filter(p => !p.parentName && p.isGranted).map(p => p.displayName || p.name)
  }
}

/**
 * The list endpoint does not carry permission counts, so they are fetched per member once the
 * table is already on screen. A failure leaves that one cell blank — it never blocks the page.
 */
const loadPermissionSummaries = async (list) => {
  const summaries = {}
  await Promise.all(
    list.map(async (user) => {
      try {
        summaries[user.id] = summarise(await userPermissionService.getUserPermissions(user.id))
      } catch (err) {
        console.error('Error loading permission summary:', err)
      }
    })
  )
  permissionSummaries.value = summaries
  summariesLoaded.value = true
}

const loadUsers = async () => {
  loading.value = true
  error.value = ''
  summariesLoaded.value = false

  try {
    users.value = (await userService.getTenantUsers()) || []
    loadPermissionSummaries(users.value)
  } catch (err) {
    console.error('Error loading users:', err)
    error.value = t('users.errors.loadFailed')
  } finally {
    loading.value = false
  }
}

/* ── Actions ───────────────────────────────────────────────── */

const clearFilters = () => {
  query.value = ''
  filter.value = 'all'
}

const openCreate = () => {
  editingUserId.value = null
  editOpen.value = true
}

const openEdit = (user) => {
  editingUserId.value = user.id
  editOpen.value = true
}

const closeEdit = () => {
  editOpen.value = false
  editingUserId.value = null
}

const editPermissions = async (userId) => {
  selectedUserId.value = userId

  // Let the prop reach the modal before Bootstrap shows it.
  await new Promise(resolve => setTimeout(resolve, 0))

  const modalElement = document.getElementById('userPermissionsModal')
  const modal = new Modal(modalElement, { focus: false })

  modalElement.addEventListener('shown.bs.modal', async () => {
    if (permissionsModal.value) {
      await permissionsModal.value.loadUserPermissions()
    }
  }, { once: true })

  modal.show()
}

/** The API reports why an operation was refused; surface that instead of the generic message. */
const apiMessage = (err, fallback) => {
  if (err?.response?.data?.error?.message) return err.response.data.error.message
  if (err?.message) return err.message
  return fallback
}

const deleteUser = async (user) => {
  const result = await Swal.fire({
    title: t('common.actions.confirm'),
    text: t('users.confirmDelete', { name: nameFor(user) }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: t('common.actions.delete'),
    cancelButtonText: t('common.actions.cancel')
  })

  if (!result.isConfirmed) return

  try {
    await userService.deleteUser(user.id)
    users.value = users.value.filter(u => u.id !== user.id)
    store.dispatch('notify', { message: t('users.deleteSuccess'), type: 'success' })
  } catch (err) {
    console.error('Error deleting user:', err)
    Swal.fire({
      title: t('common.actions.error'),
      text: apiMessage(err, t('users.errors.deleteFailed')),
      icon: 'error'
    })
  }
}

const isCurrentUser = (user) => !!currentUser.value && currentUser.value.id === user.id

const handlePermissionError = (err) => {
  Swal.fire({
    title: t('common.actions.error'),
    text: apiMessage(err, t('users.permissions.operationFailed')),
    icon: 'error'
  })
}

onMounted(loadUsers)
</script>

<style scoped>
/* ── Page-level buttons ──────────────────────────────────────── */
.us-page-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.84rem;
  border-radius: 10px;
  border: 1px solid var(--vp-gray-300);
  padding: 9px 15px;
  font-weight: 600;
  background: var(--vp-surface);
  color: var(--vp-body-color);
  white-space: nowrap;
  text-decoration: none;
  flex: 0 0 auto;
}

.us-page-btn:hover {
  background: var(--vp-gray-100);
  color: var(--vp-body-color);
}

.us-page-btn.is-primary {
  border: 0;
  background: var(--vp-accent);
  color: #fff;
  padding: 9px 16px;
}

.us-page-btn.is-primary:hover {
  background: var(--vp-info);
  color: #fff;
}

/* ── Tiles ───────────────────────────────────────────────────── */
.us-kpi-note {
  font-size: 0.74rem;
  margin-top: 3px;
}

.us-kpi-note.is-muted { color: var(--vp-gray-500); }
.us-kpi-note.is-success { color: #15705f; }
.us-kpi-note.is-warning { color: #8a6112; }

/* ── Toolbar ─────────────────────────────────────────────────── */
.us-toolbar {
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--vp-gray-200);
}

.us-card-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--vp-heading-color);
  margin: 0;
}

.us-card-count {
  color: var(--vp-gray-500);
  font-weight: 600;
}

.us-search {
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid var(--vp-gray-300);
  border-radius: 10px;
  padding: 8px 12px;
  background: #fbfcfe;
  flex: 1 1 200px;
  max-width: 320px;
  color: var(--vp-gray-500);
}

.us-search input {
  border: 0;
  background: transparent;
  outline: none;
  font-size: 0.84rem;
  width: 100%;
  color: var(--vp-body-color);
}

.us-filters {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}

.us-filter {
  border: 1px solid var(--vp-gray-300);
  background: var(--vp-surface);
  color: var(--vp-body-color);
  border-radius: var(--vp-radius-pill);
  padding: 7px 13px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.us-filter:hover {
  background: var(--vp-gray-100);
}

.us-filter.is-active {
  background: var(--vp-gray-800);
  border-color: var(--vp-gray-800);
  color: #fff;
}

/* ── Rows ────────────────────────────────────────────────────── */
.us-scroll {
  overflow-x: auto;
  max-width: 100%;
}

.us-table {
  min-width: 780px;
}

.us-head-row,
.us-row {
  display: grid;
  grid-template-columns: minmax(240px, 2fr) minmax(190px, 1.3fr) 128px;
  gap: 14px;
}

.us-head-row {
  padding: 11px 18px;
  /* Matches the rows' coloured spine so the columns line up with it. */
  border-left: 3px solid transparent;
  border-bottom: 1px solid var(--vp-gray-200);
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  font-weight: 700;
  color: var(--vp-gray-500);
  text-transform: uppercase;
}

.us-right {
  text-align: right;
}

.us-row {
  padding: 13px 18px;
  border-bottom: 1px solid #f4f7fa;
  align-items: center;
  border-left: 3px solid transparent;
  min-width: 0;
}

.us-row:hover {
  background: #fbfdff;
}

.us-identity {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.us-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(var(--vp-accent-rgb), 0.12);
  color: #0b7fa8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.us-avatar.is-muted {
  background: var(--vp-gray-200);
  color: var(--vp-gray-500);
}

.us-name-row {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.us-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--vp-heading-color);
}

.us-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: var(--vp-radius-pill);
  padding: 1px 9px;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
}

.us-pill.is-active {
  background: var(--vp-light-success);
  color: #0f7350;
}

.us-pill.is-idle {
  background: var(--vp-gray-200);
  color: var(--vp-muted-color);
}

.us-pill.is-pending {
  background: var(--vp-light-warning);
  color: #8a5a00;
}

.us-email {
  font-size: 0.76rem;
  color: var(--vp-gray-500);
  margin-top: 2px;
}

.us-phone {
  font-size: 0.74rem;
  color: var(--vp-gray-400);
  margin-top: 1px;
}

.us-areas {
  font-size: 0.8rem;
  color: var(--vp-body-color);
  text-wrap: pretty;
}

.us-areas--muted {
  color: var(--vp-gray-400);
}

.us-perm-meter {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 7px;
}

.us-perm-track {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: var(--vp-gray-200);
  overflow: hidden;
  min-width: 30px;
}

.us-perm-track i {
  display: block;
  height: 100%;
}

.us-perm-count {
  font-size: 0.72rem;
  color: var(--vp-gray-500);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.us-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.us-icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid var(--vp-gray-300);
  background: var(--vp-surface);
  color: var(--vp-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.us-icon-btn:hover {
  background: var(--vp-gray-100);
}

.us-icon-btn.is-accent {
  color: #0b7fa8;
}

.us-icon-btn.is-accent:hover {
  background: rgba(var(--vp-accent-rgb), 0.1);
  border-color: var(--vp-accent);
}

.us-icon-btn.is-danger {
  color: #c0392b;
  border-color: rgba(var(--vp-danger-rgb), 0.3);
}

.us-icon-btn.is-danger:hover {
  background: rgba(var(--vp-danger-rgb), 0.1);
}

/* ── States ──────────────────────────────────────────────────── */
.us-state {
  padding: 40px 20px;
  text-align: center;
  font-size: 0.86rem;
  color: var(--vp-gray-500);
}

.us-state--error {
  color: #c1291a;
}

.us-empty {
  padding: 46px 20px;
  text-align: center;
  color: var(--vp-gray-500);
}

.us-empty > i {
  font-size: 2rem;
  color: var(--vp-gray-400);
}

.us-empty-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--vp-muted-color);
  margin: 10px 0;
}

.us-empty-text {
  font-size: 0.82rem;
  margin-bottom: 14px;
}

/* ── Footer ──────────────────────────────────────────────────── */
.us-foot {
  padding: 13px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #fbfcfe;
}

.us-foot-note {
  font-size: 0.78rem;
  color: var(--vp-gray-500);
}

.us-invite {
  margin-left: auto;
  border: 1px dashed var(--vp-gray-400);
  background: var(--vp-surface);
  color: #0b7fa8;
  border-radius: 9px;
  padding: 8px 15px;
  font-size: 0.8rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  flex: 0 0 auto;
}

.us-invite:hover {
  background: var(--vp-gray-100);
  border-color: var(--vp-accent);
}

@media (max-width: 575.98px) {
  .us-toolbar {
    align-items: stretch;
  }

  .us-search {
    max-width: none;
  }
}
</style>
